'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Flag, Phone, Mail } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/context/AuthContext';

// TikTok icon (not in lucide-react)
function TikTokIcon({ size = 14 }: {size?: number;}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.76a4.85 4.85 0 01-1.01-.07z" />
    </svg>);

}

function InstagramIcon({ size = 14 }: {size?: number;}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
    </svg>);

}

export default function Footer() {
  const { user } = useAuth();
  const isLoggedIn = !!user;

  const allLinks = [
  { href: '/', label: 'Home' },
  { href: '/events', label: 'Events' },
  { href: '/live-results', label: 'Live Results' },
  { href: '/about', label: 'About Us' },
  { href: '/login', label: 'Sign In', authOnly: false },
  { href: '/register', label: 'Register', authOnly: false }];


  const visibleLinks = allLinks.filter((link) => {
    if ((link as any).authOnly === false && isLoggedIn) return false;
    return true;
  });

  return (
    <footer className="border-t border-border bg-card mt-auto">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <AppLogo size={32} />
              <span className="font-extrabold text-base tracking-wider uppercase text-foreground">
                SPEED<span className="text-primary">PASS</span>
              </span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">Creating experiences that bring people together, moments that leave a lasting impression, and events that move beyond the ordinary.</p>
            <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
              <Phone size={14} className="text-primary shrink-0" />
              <a href="tel:+601167637150" className="hover:text-primary transition-colors">011-67637150</a>
            </div>
            <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
              <Mail size={14} className="text-primary shrink-0" />
              <span>speedpassventures@hotmail.com</span>
            </div>
            {/* Social Media Icons */}
            <div className="flex items-center gap-3 mt-4">
              <a
                href="https://www.instagram.com/speedpass.live?igsh=MW8ybnNkbjhnNjg5eA%3D%3D&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-muted/40 border border-border text-muted-foreground hover:text-primary hover:border-primary/40 hover:bg-primary/10 transition-all"
                title="Instagram">

                <InstagramIcon size={15} />
              </a>
              <a
                href="https://www.tiktok.com/@speedpass.my"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-muted/40 border border-border text-muted-foreground hover:text-primary hover:border-primary/40 hover:bg-primary/10 transition-all"
                title="TikTok">

                <TikTokIcon size={15} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest text-foreground mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {visibleLinks.map((link) =>
              <li key={`footer-${link?.href}`}>
                  <Link
                  href={link?.href}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-1.5">
                    <Flag size={12} className="text-primary/50" />
                    {link?.label}
                  </Link>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="border-t border-border mt-8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">© 2026 SpeedPass. All rights reserved.</p>
          <p className="text-xs text-muted-foreground">
            Powered by <span className="text-primary font-semibold">SpeedPass Ventures</span>
          </p>
        </div>
      </div>
    </footer>);

}